import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {HashtagService} from "../../service/hashtag.service";
import {Hashtag} from "../../interface/hashtag";
import {UpdateHashtagRequest} from "../../interface/updateHashtagRequest";
import {onlyLettersValidator} from '../../validator/onlyLettersValidator';

@Component({
  selector: 'app-edit-hashtag',
  templateUrl: './edit-hashtag.component.html',
  styleUrl: './edit-hashtag.component.scss',
})
export class EditHashtagComponent implements OnInit {
  contentForm: FormGroup | any;
  hashtagResponse: Hashtag | any;

  constructor(private formBuilder: FormBuilder,
              private hashtagService: HashtagService,
              private dialogRef: MatDialogRef<EditHashtagComponent>,
              private snackBar: MatSnackBar,
              @Inject(MAT_DIALOG_DATA) private data: any) {
    this.hashtagService.getHashtagById(data.hashtag).subscribe({
      next: (hashtagResponse: Hashtag) => {
        this.hashtagResponse = hashtagResponse
        this.initForm()
      },
      error: (error) => {
        this.handleErrorResponse(error)
      }
    })
  }

  ngOnInit() {
    this.initForm()
  }

  closePopUp() {
    this.dialogRef.close()
  }

  onSubmit() {
    if (this.contentForm.valid) {
      this.processForm();
    } else {
      this.markFormAsTouched();
    }
  }

  get editContentControl(): any {
    return this.contentForm.controls;
  }

  private handleErrorResponse(error: any) {
    switch (error.status) {
      case 404:
        this.showSnackBar('Hashtag not found.', 'error');
        break;
      case 500:
        this.showSnackBar('Internal server error.', 'error');
        break;
      default:
        this.showSnackBar('An unexpected error occurred.', 'error');
        break;
    }
  }

  private showSnackBar(message: string, panelClass: string) {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: [panelClass]
    });
  }

  private initForm() {
    this.contentForm = this.formBuilder.group({
        name: [
          this.hashtagResponse?.name,
          [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(25),
            onlyLettersValidator()
          ],
        ],
      },
      {
        updateOn: "blur"
      }
    );
  }

  private processForm() {
    const { name } = this.contentForm.value;
    if (this.isSameHashtag(name)) {
      this.dialogRef.close();
      return;
    }
    if (!this.hashtagResponse) {
      return;
    }
    this.checkHashtagExists(name);
  }

  private isSameHashtag(name: string): boolean {
    return name === this.hashtagResponse.name;
  }

  private checkHashtagExists(name: string) {
    this.hashtagService.checkHashtagExists(name).subscribe({
      next: (exists: boolean) => this.handleCheckExistsResponse(exists, name),
      error: (error) => this.handleCheckExistsError(error)
    });
  }

  private handleCheckExistsResponse(exists: boolean, name: string) {
    if (exists) {
      this.contentForm.get('name')?.setErrors({ alreadyExists: true });
    } else {
      this.updateHashtag(name);
    }
  }

  private handleCheckExistsError(error: any) {
    this.showSnackBar('An error occurred while checking hashtag existence.', 'error');
  }

  private updateHashtag(name: string) {
    const updatedHashtag: UpdateHashtagRequest = {
      name: name,
    };
    this.dialogRef.close(updatedHashtag);
  }

  private markFormAsTouched() {
    this.contentForm.markAllAsTouched();
  }

}
